// src/components/borrowing/BorrowerClaimCard.jsx
import React, { useState } from 'react';
import { 
  AlertTriangle, MessageSquare, CheckCircle, Clock, 
  ChevronDown, ChevronUp, FileText, Calendar, User,
  DollarSign, Shield, ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReportStatus, BorrowerResponseType } from '../constants';
import BorrowerResponseModal from './BorrowerResponseModal';

const statusConfig = {
  [ReportStatus.PENDING_RESPONSE]: { 
    color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30', 
    icon: Clock, 
    label: 'Grace Period',
    urgent: true,
    message: 'Respond within 7 days to prevent public listing'
  },
  [ReportStatus.PUBLISHED_UNRESOLVED]: { 
    color: 'bg-red-500/10 text-red-500 border-red-500/30', 
    icon: AlertTriangle, 
    label: 'Public - Unresolved',
    urgent: true,
    message: 'This claim is visible to the public'
  },
  [ReportStatus.PUBLISHED_DISPUTED]: { 
    color: 'bg-orange-500/10 text-orange-500 border-orange-500/30', 
    icon: MessageSquare, 
    label: 'Disputed',
    urgent: false,
    message: 'Your dispute is on record'
  },
  [ReportStatus.PUBLISHED_SETTLED]: { 
    color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30', 
    icon: CheckCircle, 
    label: 'Settled',
    urgent: false,
    message: 'This matter has been resolved'
  },
};

const BorrowerClaimCard = ({ report, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  
  const config = statusConfig[report.status] || statusConfig[ReportStatus.PUBLISHED_UNRESOLVED];
  const StatusIcon = config.icon;
  
  const canRespond = !report.borrowerResponse && 
    report.status !== ReportStatus.PUBLISHED_SETTLED &&
    report.status !== ReportStatus.WITHDRAWN;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      if (dateString.seconds) {
        return new Date(dateString.seconds * 1000).toLocaleDateString();
      }
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const calculateDaysRemaining = () => {
    if (report.status !== ReportStatus.PENDING_RESPONSE) return null;
    const created = report.createdDate?.seconds 
      ? new Date(report.createdDate.seconds * 1000) 
      : new Date(report.createdDate);
    const now = new Date();
    const diffTime = 7 * 24 * 60 * 60 * 1000 - (now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const daysRemaining = calculateDaysRemaining();

  return (
    <>
      <div className={`bg-slate-800 rounded-xl border overflow-hidden shadow-lg transition-all ${
        config.urgent ? 'border-l-4 border-l-red-500 border-slate-700' : 'border-slate-700'
      }`}>
        
        {/* Urgent Banner */}
        {config.urgent && report.status === ReportStatus.PENDING_RESPONSE && (
          <div className="bg-yellow-500/10 border-b border-yellow-500/30 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-yellow-400">
              <Clock size={14} className="animate-pulse" />
              <span className="text-xs font-bold uppercase">
                {daysRemaining} Day{daysRemaining !== 1 ? 's' : ''} Left to Respond
              </span>
            </div>
            <button
              onClick={() => setShowResponseModal(true)}
              className="text-xs bg-yellow-500 text-slate-900 px-3 py-1 rounded-full font-bold hover:bg-yellow-400 transition-colors"
            >
              Respond Now
            </button>
          </div>
        )}

        {/* Header */}
        <div className="p-4">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${config.color}`}>
                <StatusIcon size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${config.color}`}>
                    {config.label}
                  </span>
                  <span className="text-xs text-slate-500">
                    Ref: {report.id?.substring(0, 8).toUpperCase()}
                  </span>
                </div>
                <p className="text-white font-bold mt-1">
                  Claim by {report.lenderName || 'Unknown Lender'}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{config.message}</p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-2xl font-bold font-mono text-red-400">
                R{report.amount?.toLocaleString()}
              </p>
              <p className="text-xs text-slate-500">Claimed Amount</p>
            </div>
          </div>

          {/* Quick Info */}
          <div className="flex flex-wrap gap-4 mt-4 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              Due: {report.dueDate}
            </span>
            <span className="flex items-center gap-1">
              <FileText size={12} />
              Filed: {formatDate(report.createdDate || report.createdAt)}
            </span>
            {report.evidenceAttachments?.length > 0 && (
              <span className="flex items-center gap-1 text-emerald-400">
                <FileText size={12} />
                {report.evidenceAttachments.length} Evidence File{report.evidenceAttachments.length > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Expandable Details */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full mt-4 pt-3 border-t border-slate-700 flex items-center justify-between text-sm text-slate-400 hover:text-white transition-colors"
          >
            <span>{expanded ? 'Hide Details' : 'View Full Claim'}</span>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {expanded && (
            <div className="mt-4 space-y-4 animate-in slide-in-from-top-2">
              {/* Claim Description */}
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-xs text-slate-500 uppercase font-bold mb-2">Lender's Statement</p>
                <p className="text-sm text-slate-300 italic">"{report.description}"</p>
              </div>

              {/* Your Response (if exists) */}
              {report.borrowerResponse && (
                <div className={`rounded-lg p-4 border ${
                  report.borrowerResponseType === BorrowerResponseType.DISPUTE 
                    ? 'bg-orange-900/20 border-orange-500/30'
                    : report.borrowerResponseType === BorrowerResponseType.CLAIM_PAID
                      ? 'bg-emerald-900/20 border-emerald-500/30'
                      : 'bg-blue-900/20 border-blue-500/30'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase font-bold text-slate-400">Your Response</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      report.borrowerResponseType === BorrowerResponseType.DISPUTE 
                        ? 'bg-orange-500 text-white'
                        : report.borrowerResponseType === BorrowerResponseType.CLAIM_PAID
                          ? 'bg-emerald-500 text-white'
                          : 'bg-blue-500 text-white'
                    }`}>
                      {report.borrowerResponseType?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300">"{report.borrowerResponse}"</p>
                  <p className="text-xs text-slate-500 mt-2">{report.borrowerResponseDate}</p>
                </div>
              )}

              {/* Lender Reply (if exists) */}
              {report.lenderReply && (
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
                  <p className="text-xs text-slate-500 uppercase font-bold mb-2">Lender Reply</p>
                  <p className="text-sm text-slate-300 italic">"{report.lenderReply}"</p>
                  <p className="text-xs text-slate-500 mt-2">{report.lenderReplyDate}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions Footer */}
        <div className="px-4 py-3 bg-slate-900/50 border-t border-slate-700 flex items-center justify-between">
          <Link
            to={`/manage/${report.id}`}
            className="text-sm text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
          >
            View Full Thread
            <ExternalLink size={12} />
          </Link>

          {canRespond ? (
            <button
              onClick={() => setShowResponseModal(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors"
            >
              <MessageSquare size={16} />
              Respond to Claim
            </button>
          ) : report.borrowerResponse ? (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <CheckCircle size={12} className="text-emerald-500" />
              Response Submitted
            </span>
          ) : null}
        </div>
      </div>

      {/* Response Modal */}
      <BorrowerResponseModal
        isOpen={showResponseModal}
        onClose={() => setShowResponseModal(false)}
        report={report}
        onResponseSubmitted={() => {
          setShowResponseModal(false);
          onUpdate?.();
        }}
      />
    </>
  );
};

export default BorrowerClaimCard;